<template>
  <div class="registration-block">

    <div class="block">
        <logged-on v-if="isAuth"/>

        <v-row v-else class="unit">
          <v-col cols="12" sm="6">
            <h2 class="mb-0">{{ $t('auth.createAccount') }}</h2>
          </v-col>

          <v-col cols="12" sm="6">
            <div class="login">
              <span>{{ $t('auth.signInAsk') }}</span>
              <span class="link-b" @click="goToLogIn()" data-attr="sign-in">{{ $t('auth.signInAccount') }}</span>
            </div>
          </v-col>
        </v-row>

        <div v-if="flow === $options.FLOW.TRIAL && (!isAuth || !showInitialData) && !isVirtualHostingV2" class="info-message valid mt-n6 pt-0">
          <v-icon class="sr-icon-check"/>
          <span v-html="$t('auth.registrationAlert')"></span>
        </div>

        <v-form ref="auth">
          <template v-if="!isAuth">
            <v-row class="unit" align="start">

              <v-col cols="12" md="6" data-attr="reg-name">
                <v-text-field
                    :label="$t('auth.name')"
                    v-model.trim="auth.name"
                    outlined
                    :rules="rulesName"
                    @keyup.enter="verifyEmail"
                >
                  <template v-slot:label>
                    <span v-html="$t('auth.name')"></span>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12" md="6" data-attr="reg-email" class="verify-email">
                <v-text-field
                    :label="$t('auth.email')"
                    v-model.trim="auth.email"
                    outlined
                    :rules="rulesEmail"
                    @keyup.enter="verifyEmail"
                    validate-on-blur
                />
              </v-col>

              <v-expand-transition>
                <template v-if="authError">
                  <div class="info-message invalid py-0 px-3" data-attr="auth-invalid">
                    <v-icon class="sr-icon-close"/>
                    <span class="error-message" v-html="$t('validation.'+authError)"></span>
                  </div>
                </template>
              </v-expand-transition>

            </v-row>

            <p class="mb-6" v-if="!isTrialFlow">{{ $t('auth.orderConfirmation') }}</p>
          </template>

          <v-row v-if="flow === $options.FLOW.DOMAIN" class="unit" data-attr="fill-details">
            <v-col cols="12">
              <v-switch
                  v-model="fillDetails"
                  inset
                  :label="$t('auth.skipFillInfo')"
                  :true-value="false"
                  :false-value="true"
              >
              </v-switch>

              <div class="info-message valid modify">
                <span v-html="$t('auth.secureHint')"></span>
              </div>
            </v-col>
          </v-row>
        </v-form>

        <template v-if="showData">
          <v-form ref="initialData">
            <v-row class="unit">

              <template v-if="!showInitialData">
                <v-col cols="12" md="6" data-attr="reg-last-name">
                  <v-text-field
                      v-model.trim="initialData.lastname"
                      outlined
                      :rules="rulesLastName"
                  >
                    <template v-slot:label>
                      <span v-html="$t('auth.lastName')"></span>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col v-if="!isTrialFlow" cols="12" md="6" data-attr="company">
                  <v-text-field
                      :label="$t('auth.companyName')"
                      v-model.trim="initialData.company"
                      :rules="rulesCompanyName"
                      outlined
                  />
                </v-col>

                <v-col cols="12" md="6" data-attr="reg-registrant-street">
                  <v-text-field
                      :label="$t('auth.registrantStreet')"
                      v-model.trim="initialData.address"
                      outlined
                      :rules="rulesAddress"
                  />
                </v-col>

                <v-col cols="12" md="6" data-attr="reg-registrant-city">
                  <v-text-field
                      :label="$t('auth.registrantCity')"
                      v-model.trim="initialData.city"
                      outlined
                      :rules="rulesCity"
                  />
                </v-col>

                <v-col v-if="!isTrialFlow" cols="12" md="6" data-attr="reg-registrant-state">
                  <v-text-field
                      :label="$t('auth.registrantState')"
                      v-model.trim="initialData.state"
                      outlined
                      :rules="rulesState"
                  />
                </v-col>

                <v-col v-if="!isTrialFlow" cols="12" md="6" data-attr="reg-registrant-zip">
                  <v-text-field
                      :label="$t('auth.registrantZip')"
                      v-model.trim="initialData.postcode"
                      outlined
                      :rules="rulesPostcode"
                  />
                </v-col>
              </template>

              <verification
                  v-model="verification"
                  ref="verification"
                  :auth="authData"
                  :flow="flow"
                  :parentClass="'.registration-block'"
              />

            </v-row>
          </v-form>
        </template>

        <v-row class="unit">

          <v-col cols="auto" v-if="!(isTrialFlow && isAuth)">
            <v-checkbox
                v-model="auth.newsletter"
                :label="$t('auth.receiveNewsletter')"
                :ripple="false"
                color="primary"
                data-attr="reg-receive-newsletter"
            />
          </v-col>

          <v-col cols="12" class="submit" v-if="!isTrialFlow">
            <v-btn
                color="primary"
                dark
                @click="verifyEmail"
                data-attr="signin-btn"
            >
              {{ $t('actions.continue') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

    <popup-loader :show-popup="showPopupLoader" :title="$t('auth.registrationText')"/>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import { SETTINGS_GETTERS, SETTINGS_MUTATIONS, SETTING_ACTIONS } from "@/store/modules/settings";
  import { AUTH_MUTATIONS } from "@/store/modules/auth";
  import PopupLoader from '@/shared/components/Loaders/PopupLoader.vue';
  import { verifyEmail, registration, updateClient } from "@/store/api/auth";
  import { AUTH_ERROR, COUNTRIES, FLOW } from '@/constants';
  import * as Sentry from "@sentry/vue";
  import LoggedOn from "./LoggedOn.vue";
  import Verification from "./Verification.vue";

  export default {
    name: "Registration",
    FLOW,
    components: {
      PopupLoader,
      LoggedOn,
      Verification,
    },
    props: {
      isAuth: { type: Boolean, default: false },
      flow: { type: String },
      isVirtualHostingV2: { type: Boolean, default: false },
    },
    data: () => ({
      loading: false,
      showPopupLoader: false,
      authError: undefined,
      showInitialData: false,
      fillDetails: false,
      verification: {
        country: 'UA',
        phonenumber: '',
        verifytype: 'sms'
      },
      authData: undefined,
    }),
    created() {
      this.$nextTick(() => {
        this.fillDetails = this.isVirtualHostingV2 ? false : this.flow === FLOW.TRIAL;
      });
    },
    computed: {
      ...mapGetters('settings', {
        authorization: SETTINGS_GETTERS.AUTH,
      }),
      showData() {
        if(this.isVirtualHostingV2) { return false };
        return this.fillDetails;
      },
      auth() {
        return {
          name: this.isAuth ? this.authorization.auth.name : '',
          email: this.isAuth ? this.authorization.auth.email : '',
          newsletter: this.isAuth && this.authorization.auth.newsletter ? this.authorization.auth.newsletter : true,
        }
      },
      initialData() {
        return {
          lastname: this.isAuth ? this.authorization.auth.lastname : '',
          company: this.isAuth ? this.authorization.auth.company : '',
          address: this.isAuth ? this.authorization.auth.address : '',
          city: this.isAuth ? this.authorization.auth.city : '',
          state: this.isAuth ? this.authorization.auth.state : '',
          postcode: this.isAuth ? this.authorization.auth.postcode : '',
        }
      },
      rulesName() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v.trim() || this.$t('validation.requiredName'));
        rules.push(v => (v || '' ).length <= 200 || this.$t('validation.maxChars', {chars: 200}));
        return rules;
      },
      rulesEmail() {
        const rules = this.$attrs.rules || [];
        rules.push(v => v && !!v.trim() || this.$t('validation.requiredEmail'));
        return rules;
      },
      rulesLastName() {
        const rules = this.$attrs.rules || [];
        rules.push(v => v && !!v.trim() || this.$t('validation.requiredLastName'));
        rules.push(v => (v || '' ).length <= 200 || this.$t('validation.maxChars', {chars: 200}));

        return rules;
      },
      rulesCompanyName() {
        const rules = this.$attrs.rules || [];
        rules.push(v => (v || '' ).length <= 1000 || this.$t('validation.maxChars', {chars: 1000}));
        return rules;
      },
      rulesState() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v.trim() || this.$t('validation.requiredState'));
        rules.push(v => (v || '' ).length <= 500 || this.$t('validation.maxChars', {chars: 500}));

        return rules;
      },
      rulesPostcode() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v.trim() || this.$t('validation.requiredPostcode'));
        rules.push(v => (v || '' ).length <= 100 || this.$t('validation.maxChars', {chars: 100}));

        return rules;
      },
      rulesCity() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v.trim() || this.$t('validation.requiredCity'));
        rules.push(v => (v || '' ).length <= 1000 || this.$t('validation.maxChars', {chars: 1000}));

        return rules;
      },
      rulesAddress() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v.trim() || this.$t('validation.requiredAddress'));
        rules.push(v => (v || '' ).length <= 1000 || this.$t('validation.maxChars', {chars: 1000}));

        return rules;
      },
      isTrialFlow() {
        return this.flow === FLOW.TRIAL;
      },
    },
    methods: {
      ...mapActions('trialAuth', {
        setTrialAuth: SETTING_ACTIONS.SET_TRIAL_AUTH,
      }),
      goToLogIn() {
        this.$emit('getLogIn', true);
      },
      isFormValidate() {
        let validate = this.showInitialData || this.$refs.auth.validate();
        let initialDataValidate = false;
        let verificationValidate = false;

        if(this.fillDetails) {
          const authValidate = this.$refs.auth.validate();

          if(this.isVirtualHostingV2) {
            initialDataValidate = true;
            verificationValidate = true;
          } else {
            initialDataValidate = this.isAuth && this.isTrialFlow && this.showInitialData || this.$refs.initialData.validate();
            verificationValidate = this.$refs.verification.validate();
          }

          validate = authValidate && initialDataValidate && verificationValidate;
        }
        if (!validate) {
          this.$nextTick(() => {
            const el = this.$el.querySelector(".v-messages.error--text:first-child");
            if(el) { this.$vuetify.goTo(el, { offset: 80 }) };
          });
        }
        return validate;
      },
      requestData() {
        if(this.isTrialFlow) {
          if(this.isAuth) {
            delete this.auth.newsletter
          }
        }

        this.auth.fill_details = this.fillDetails;
        const geoCookie = this.getGeoCookie();
        const data = this.fillDetails ? {...this.auth, ...this.initialData, ...this.verification} : { ...this.auth, ...geoCookie };
        return data;
      },
      getGeoCookie() {
        const phonenumber = document.cookie && (document.cookie.split(";").find(cook => cook.match(/geo_phonecode=/)) || '').split("=")[1] || '';
        const country = document.cookie && (document.cookie.split(";").find(cook => cook.match(/geo_country=/)) || '').split("=")[1] || '';
        return { phonenumber, country }
      },
      async verifyEmail() {
        this.authError = undefined;
        this.isFormValidate();

        if(!this.auth.email) return
        this.loading = true;
        try {
          const result = await verifyEmail(this.auth.email);
          this.submit();
        } catch (errors) {
          Sentry.captureException(errors);
          this.authError = AUTH_ERROR.MESSAGE[errors?.data?.message?.[0]];
          this.$nextTick(() => {
            const el = this.$el.querySelector(".verify-email");
            if(el) { this.$vuetify.goTo(el, { offset: 80 }) };
          });
        } finally {
          this.loading = false;
        }
      },
      async submit() {
        const valid = this.isFormValidate();

        if(valid) {
          this.loading = true;
          this.showPopupLoader = true;
          this.authError = undefined;

          const WHMCS = document.cookie && (document.cookie.split(";").find(cook => cook.match(/WHMCS[a-zA-Z0-9]{12}=/)) || '').split("=")[0] || '';
          document.cookie = `${WHMCS}=;max-age=31536000;path=/`;

          try {
            const updateUser = this.isAuth && this.isTrialFlow;
            const result = updateUser ? await updateClient(this.requestData(), this.authorization.cookie) : await registration(this.requestData());

            const now = new Date();
            const expireTime = now.setFullYear(now.getFullYear() + 1);
            now.setTime(expireTime);

            if(!updateUser) {
              document.cookie = `WHMCSUser=${result.authcookie};expires=${now.toUTCString()};max-age=31536000;path=/`;
              const setAuth = { cookie: result.authcookie, auth: result.client }
              this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_AUTH}`, setAuth);
            }
            this.gtmEvent();
            this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_TRIAL_AUTH}`, true);
          } catch (errors) {
            Sentry.captureException(errors);
            this.authError = AUTH_ERROR.MESSAGE[errors?.data?.message?.[0]];
          } finally {
            this.loading = false;
            this.showPopupLoader = false;
          }
        }
        return valid;
      },
      initialDataValidate() {
        let validate = false;
        if(this.isAuth && this.isTrialFlow) {
          if(this.isVirtualHostingV2) {
            validate = true;
          } else {
            validate = this.$refs.initialData.validate();
          }

          if(!validate) {
            setTimeout(() => {
              this.showInitialData = true;
            }, 100)
            setTimeout(() => {
              this.showInitialData = false;
            }, 200)

          }
        }
        this.showInitialData = validate;
      },
      gtmEvent() {
        window.dataLayer.push({
          event: 'autoEvent',
          eventCategory: 'Site usage',
          eventAction: 'Registration',
          eventLabel: 'Main Order Form',
        });
      }
    },
    watch: {
      isAuth(val) {
        this.authError = undefined;
        setTimeout(() => { this.initialDataValidate() }, 1000)
        this.authData = this.authorization.auth;
      },
      verification: {
        handler(val) {
          this.$store.commit(`auth/${AUTH_MUTATIONS.SET_VERIFICATION}`, val);
        },
        deep: true,
      },
      fillDetails(val) {
        if(!val) {
          this.$store.commit(`auth/${AUTH_MUTATIONS.SET_VERIFICATION}`, undefined);
        }
      },
      isVirtualHostingV2(val) {
        this.fillDetails = this.isVirtualHostingV2 ? false : this.flow === FLOW.TRIAL;
        this.showInitialData = false;
        setTimeout(() => { this.initialDataValidate() }, 1000)
      }
    }
  }
</script>

<style lang="scss">
  .registration-block {

    .v-input--switch {

      label {
        font-weight: 700;
      }
    }

    .info-message.mt-n6.pt-0 {
      font-weight: 700;
      padding-bottom: 31px;
    }
  }

  .login {
    font-size: 16px;
    text-align: right;

    @media (max-width: $phablet) {
      text-align: left;
    }

    .link-b {
      margin-left: 5px;
    }
  }

  .submit {
    @media (max-width: $phablet) {
      order: 2;
    }
  }

  .order-dialog-form {

    .dialog-content {

      @media (min-width: $tablet) {
        padding: 50px 40px;
      }
    }

    h3 {
      margin-bottom: 6px;
    }
  }
</style>
